import {
  AnalyticsUserProperties,
  CampaignDetailsEvent,
  GA_EVENTS,
} from '../models/AnalyticsModel';

// ONLY THESE KEYS ARE EVER READ FROM A URL, SO NAMES, EMAILS OR OTHER FORM
// VALUES THAT END UP IN A QUERY STRING CAN NEVER REACH ANALYTICS
export const ATTRIBUTION_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gclid',
  'fbclid',
  'pp_ref',
  'pp_path',
] as const;

export type AttributionParam = (typeof ATTRIBUTION_PARAMS)[number];

export type IAttribution = Record<AttributionParam, string | null>;

export const PROMO_TOKEN_PARAM = 'token';

// GA4 TRUNCATES PARAMETER VALUES AT 100 CHARACTERS
const MAX_VALUE_LENGTH = 100;

const SOURCE_PARAMS: Array<AttributionParam> = [
  'utm_source',
  'utm_campaign',
  'gclid',
  'fbclid',
  'pp_ref',
];

const decodeComponent = (value: string): string | null => {
  try {
    return decodeURIComponent(value.replace(/\+/g, ' '));
  } catch {
    return null;
  }
};

// URLSearchParams IS INCOMPLETE ON REACT NATIVE, SO THE QUERY IS PARSED HERE
const parseQuery = (query: string): Record<string, string> =>
  query
    .replace(/^[^?]*\?/, '')
    .replace(/#.*$/, '')
    .split('&')
    .reduce<Record<string, string>>((params, pair) => {
      const separatorIndex = pair.indexOf('=');
      const hasValue = separatorIndex > 0;
      if (!hasValue) {
        return params;
      }

      const key = decodeComponent(pair.slice(0, separatorIndex));
      const value = decodeComponent(pair.slice(separatorIndex + 1));
      if (key === null || value === null) {
        return params;
      }

      return { ...params, [key]: value };
    }, {});

const normalizeValue = (value: string | null): string | null => {
  const trimmedValue = value?.trim() ?? '';
  const isUnsafeValue = trimmedValue === '' || trimmedValue.includes('@');
  if (isUnsafeValue) {
    return null;
  }

  return trimmedValue.slice(0, MAX_VALUE_LENGTH);
};

export const createEmptyAttribution = (): IAttribution =>
  ATTRIBUTION_PARAMS.reduce(
    (attribution, key) => ({ ...attribution, [key]: null }),
    {} as IAttribution,
  );

export const hasAttribution = (attribution: IAttribution | null): boolean =>
  attribution !== null &&
  SOURCE_PARAMS.some((key) => attribution[key] !== null);

// ACCEPTS A FULL URL OR A BARE QUERY STRING
export const parseAttribution = (query: string): IAttribution | null => {
  const params = parseQuery(query);
  const attribution = ATTRIBUTION_PARAMS.reduce(
    (parsed, key) => ({ ...parsed, [key]: normalizeValue(params[key] ?? null) }),
    createEmptyAttribution(),
  );

  if (!hasAttribution(attribution)) {
    return null;
  }

  return attribution;
};

export const parsePromoToken = (query: string): string | null =>
  normalizeValue(parseQuery(query)[PROMO_TOKEN_PARAM] ?? null);

const toQueryString = (entries: Array<[string, string | null]>): string =>
  entries
    .filter((entry): entry is [string, string] => entry[1] !== null)
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
    )
    .join('&');

export const buildAttributionQuery = (attribution: IAttribution | null) =>
  toQueryString(
    ATTRIBUTION_PARAMS.map((key) => [key, attribution?.[key] ?? null]),
  );

// THE QUERY HANDED OVER THROUGH THE PLAY INSTALL REFERRER
export const buildHandoffQuery = (
  attribution: IAttribution | null,
  promoToken: string | null,
) =>
  toQueryString([
    ...ATTRIBUTION_PARAMS.map((key): [string, string | null] => [
      key,
      attribution?.[key] ?? null,
    ]),
    [PROMO_TOKEN_PARAM, promoToken],
  ]);

const getClickIdSource = (attribution: IAttribution): string | null => {
  if (attribution.gclid) {
    return 'google';
  }

  if (attribution.fbclid) {
    return 'facebook';
  }

  return null;
};

// MIRRORS GA4'S OWN DEFAULTS WHEN A LINK CARRIES NO UTM TAGS
const getFallbackMedium = (attribution: IAttribution): string | null => {
  if (attribution.gclid) {
    return 'cpc';
  }

  if (attribution.fbclid) {
    return 'paid_social';
  }

  if (attribution.pp_ref) {
    return 'referral';
  }

  return null;
};

export const getAttributionSource = (attribution: IAttribution) =>
  attribution.utm_source ?? getClickIdSource(attribution) ?? attribution.pp_ref;

export const getAttributionMedium = (attribution: IAttribution) =>
  attribution.utm_medium ?? getFallbackMedium(attribution);

export const toCampaignDetailsEvent = (
  attribution: IAttribution,
): CampaignDetailsEvent => ({
  event: GA_EVENTS.campaignDetails,
  source: getAttributionSource(attribution),
  medium: getAttributionMedium(attribution),
  campaign: attribution.utm_campaign,
  term: attribution.utm_term,
  content: attribution.utm_content,
});

// FIRST TOUCH COMES FROM THE INSTALL, LAST TOUCH FROM LINKS OPENED AFTERWARDS
export const toFirstTouchUserProperties = (
  attribution: IAttribution,
): AnalyticsUserProperties => ({
  first_source: getAttributionSource(attribution),
  first_medium: getAttributionMedium(attribution),
  first_campaign: attribution.utm_campaign,
  referring_site: attribution.pp_ref,
});

export const toLastTouchUserProperties = (
  attribution: IAttribution,
): AnalyticsUserProperties => ({
  last_source: getAttributionSource(attribution),
  last_campaign: attribution.utm_campaign,
  referring_site: attribution.pp_ref,
});
